import { useMutation, useQuery } from '@tanstack/react-query'
import { apiBaseUrl } from '../api/client'

// Hooks for the printed intake address's public page (spec 2026-09-11-11).
//
// `/r/{orgCode}/{address}` is the anonymous door of ONE intake endpoint — the
// address a manager copies off the intake-addresses page and prints in an AG
// convocation. Unlike `/c/{orgCode}/{token}` no thread exists yet: this is
// where one is born.
//
// NO Authorization header is ever sent, the same rule the QR scan page and the
// resident conversation page follow: the reader has no account, and a stale
// token out of localStorage would be both useless and a leak.

export interface ResidentIntakeView {
    org_display_name: string
    asset_name?: string
    /** The address was taken out of service; the page says so instead of offering a form. */
    closed: boolean
}

export interface ResidentIntakeResult {
    acknowledged: boolean
    /** `/c/{orgCode}/{replyToken}` — the writer's own return address. */
    conversation_url?: string
}

const intakeBase = (orgCode: string, address: string) =>
    `${apiBaseUrl}/r/${encodeURIComponent(orgCode)}/${encodeURIComponent(address)}`

/**
 * intakeFetch mirrors useLabels' scanFetch: every unresolvable request — an
 * unknown address, the desk queue, a deleted organization — comes back as a
 * bare 404 with a non-JSON body, so the error path never assumes one.
 */
async function intakeFetch<T>(url: string, options?: RequestInit): Promise<T> {
    const res = await fetch(url, options)
    if (!res.ok) {
        const body = await res.json().catch(() => ({}))
        throw { status: res.status, ...body }
    }
    return res.json()
}

export const residentIntakeKey = (orgCode: string, address: string) =>
    ['resident-intake', orgCode, address] as const

export function useResidentIntakeState(orgCode: string, address: string) {
    return useQuery({
        queryKey: residentIntakeKey(orgCode, address),
        queryFn: () => intakeFetch<ResidentIntakeView>(`${intakeBase(orgCode, address)}/state`),
        enabled: !!orgCode && !!address,
        retry: false,
    })
}

export interface ResidentIntakeMessageInput {
    body: string
    contact_email?: string
    contact_phone?: string
    files?: File[]
    voice?: Blob | null
}

/** Safari records mp4/aac, everyone else webm/opus; the extension has to match
 * or the referential stores a file no player will open. */
function voiceFileName(blob: Blob): string {
    return blob.type.includes('mp4') ? 'voice-message.m4a' : 'voice-message.webm'
}

export function useSendResidentIntakeMessage(orgCode: string, address: string) {
    return useMutation({
        mutationFn: (input: ResidentIntakeMessageInput) => {
            const url = `${intakeBase(orgCode, address)}/messages`
            const files = input.files ?? []

            // JSON while there is nothing to carry, multipart the moment there
            // is — one route, two shapes, exactly as `/c/` and `/q/` post.
            if (files.length === 0 && !input.voice) {
                return intakeFetch<ResidentIntakeResult>(url, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        body: input.body,
                        contact_email: input.contact_email,
                        contact_phone: input.contact_phone,
                    }),
                })
            }

            const form = new FormData()
            form.append('body', input.body)
            if (input.contact_email) form.append('contact_email', input.contact_email)
            if (input.contact_phone) form.append('contact_phone', input.contact_phone)
            for (const file of files) form.append('files', file, file.name)
            if (input.voice) form.append('voice', input.voice, voiceFileName(input.voice))

            // No Content-Type header on purpose: only the browser knows the
            // multipart boundary it just generated.
            return intakeFetch<ResidentIntakeResult>(url, { method: 'POST', body: form })
        },
    })
}

/** Crockford base32: digits plus uppercase letters minus I, L, O and U. */
const LABEL_CODE_ALPHABET = '0123456789ABCDEFGHJKMNPQRSTVWXYZ'
const LABEL_CODE_LENGTH = 7

/**
 * isLabelBackedWebAddress mirrors the server's discriminator
 * (conversations.IsLabelBackedWebAddress): a `web` intake address is one of
 * four shapes, and only the 7-symbol label code gets a public page. The desk
 * (`{org}-desk`), the voice surface (`voice-{org}`) and a generated
 * `{org}-{10}` token are the manager's own capture paths and have no URL to
 * print — the server 404s them, so offering one here would only hand a manager
 * a link that does not work.
 *
 * Separators are deliberately NOT stripped: a three-letter organization's
 * `abc-desk` would otherwise strip to seven valid Crockford symbols.
 */
export function isLabelBackedWebAddress(address: string | null | undefined): boolean {
    const raw = (address ?? '').trim().toUpperCase()
    if (raw.length !== LABEL_CODE_LENGTH) return false
    for (const char of raw) {
        const folded = char === 'I' || char === 'L' ? '1' : char === 'O' ? '0' : char === 'U' ? 'V' : char
        if (!LABEL_CODE_ALPHABET.includes(folded)) return false
    }
    return true
}

/**
 * residentIntakeUrl is the printable link for a label-backed web address, and
 * null for every other endpoint — email and WhatsApp addresses ARE the
 * connection detail, and the three internal web surfaces have no public page.
 *
 * Built from window.location.origin so a link copied while reading the app
 * through a tunnel points back at the host the reader is actually on.
 */
export function residentIntakeUrl(orgCode: string, channel?: string, address?: string | null): string | null {
    if (channel !== 'web' || !address || !isLabelBackedWebAddress(address)) return null
    const origin = typeof window === 'undefined' ? '' : window.location.origin

    return `${origin}/r/${encodeURIComponent(orgCode)}/${encodeURIComponent(address.trim().toLowerCase())}`
}
